import React, { useCallback } from 'react';

import { ApolloError } from '@apollo/client';
import { Text } from '@rbilabs/universal-components';
import { useIntl } from 'react-intl';

import Link from 'components/link';
import useEffectOnUpdates from 'hooks/use-effect-on-updates';
import { ModalCb } from 'hooks/use-error-modal';
import { useDismissAllAndNavigate } from 'navigation/hooks/use-dismiss-and-navigate';
import { routes } from 'navigation/routing';
import {
  CONTACT_US_ROUTE_KEY,
  getErrorDialogOptions,
  getOrderFailureMessageKey,
} from 'pages/cart/payment/get-error-dialog-options';
import { GraphQLErrorCodes, isPspRefusedError, parseGraphQLErrorCodes } from 'utils/errors';
import { GraphQLError } from 'utils/network';

export interface IHandleCommitErrorInput {
  commitError: ApolloError | GraphQLError | undefined;
  dismissErrorDialog: () => void;
  errorMsgId?: string;
  onCoolingPeriodConfirm: () => void;
  openErrorDialog: ModalCb;
  confirmationTextId?: 'placeOrder' | 'continue';
  fdAccountId: string;
}

export const useHandleCommitError = ({
  commitError,
  dismissErrorDialog,
  errorMsgId,
  onCoolingPeriodConfirm,
  openErrorDialog,
  confirmationTextId = 'placeOrder',
  fdAccountId,
}: IHandleCommitErrorInput) => {
  const { formatMessage } = useIntl();
  const dismissAndNavigate = useDismissAllAndNavigate();

  const handleCommitError = useCallback(
    (error: ApolloError | GraphQLError) => {
      if (isPspRefusedError(error)) {
        return;
      }

      let errorDialog;
      const errorCode = parseGraphQLErrorCodes(error)[0]?.errorCode;

      if (errorCode === GraphQLErrorCodes.COMMIT_ORDER_COOLING_PERIOD) {
        errorDialog = {
          body: formatMessage({ id: 'errorDialogCooldownMessage' }),
          title: formatMessage({ id: 'errorDialogCooldownTitle' }),
          message: formatMessage({ id: 'errorDialogCooldownMessage' }),
          dismissText: formatMessage({ id: 'viewRecentOrders' }),
          confirmationText: formatMessage({ id: confirmationTextId }),
          onDismiss: () => dismissAndNavigate(routes.orders),
          onConfirm: () => {
            dismissErrorDialog();
            onCoolingPeriodConfirm();
          },
        };
      } else {
        const errorMessageId = errorMsgId || getOrderFailureMessageKey(fdAccountId, error);
        const body = (
          <Text>
            {formatMessage(
              { id: errorMessageId },
              {
                link: (
                  <Link
                    isInline
                    isUnderlined
                    to={CONTACT_US_ROUTE_KEY}
                    onPress={dismissErrorDialog}
                  >
                    {formatMessage({ id: 'here' })}
                  </Link>
                ),
              }
            )}
          </Text>
        );
        errorDialog = getErrorDialogOptions({
          body,
          error,
          formatMessage,
          errorMsgId: errorMessageId,
        });
      }

      openErrorDialog(errorDialog);
    },
    [
      openErrorDialog,
      formatMessage,
      confirmationTextId,
      dismissAndNavigate,
      dismissErrorDialog,
      onCoolingPeriodConfirm,
      errorMsgId,
      fdAccountId,
    ]
  );

  useEffectOnUpdates(() => {
    if (commitError) {
      handleCommitError(commitError);
    }
  }, [commitError]);

  return { handleCommitError };
};
